import React, { useState, useEffect } from 'react';
import { Icon } from '../../Icon';
import { parseBrazilianNumber } from '../../../utils/formatUtils';
import { FormField, ResultCard } from './shared';

const CurrencyConverter = ({ onResultChange }: { onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [amount, setAmount] = useState('100');
  const [from, setFrom] = useState('USD');
  const [to, setTo] = useState('BRL');
  const [rates, setRates] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    fetchRates();
  }, []);

  const fetchRates = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://economia.awesomeapi.com.br/last/USD-BRL,EUR-BRL,BTC-BRL,GBP-BRL');
      const data = await response.json();
      setRates(data);
    } catch (error) {
      console.error('Erro ao buscar taxas:', error);
    } finally {
      setLoading(false);
    }
  };

  const convert = () => {
    if (!rates) return;
    const val = parseFloat(parseBrazilianNumber(amount)) || 0;
    let rate = 1;

    if (from === 'USD' && to === 'BRL') rate = parseFloat(rates.USDBRL.bid);
    else if (from === 'EUR' && to === 'BRL') rate = parseFloat(rates.EURBRL.bid);
    else if (from === 'GBP' && to === 'BRL') rate = parseFloat(rates.GBPBRL.bid);
    else if (from === 'BTC' && to === 'BRL') rate = parseFloat(rates.BTCBRL.bid);
    else if (from === 'BRL' && to === 'USD') rate = 1 / parseFloat(rates.USDBRL.bid);
    else if (from === 'BRL' && to === 'EUR') rate = 1 / parseFloat(rates.EURBRL.bid);

    const converted = val * rate;
    setResult({ converted, rate, from, to });

    onResultChange([
      { label: 'Valor Original', value: `${from} ${val.toLocaleString()}` },
      { label: 'Taxa de Câmbio', value: rate.toFixed(4) },
      { label: 'Valor Convertido', value: `${to} ${converted.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` }
    ]);
  };

  return (
    <div className="space-y-8">
      <div className="bg-theme-result p-6 rounded-2xl border border-theme-border text-center">
        <h2 className="text-xl font-black text-theme-text mb-2 tracking-tight">Conversor de Moedas</h2>
        <p className="text-theme-muted text-sm">Taxas em tempo real via AwesomeAPI.</p>
      </div>

      <div className="bg-theme-result p-8 rounded-3xl border border-theme-border shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FormField label="Valor" value={amount} onChange={setAmount} isNumeric required />
          <FormField label="De" value={from} onChange={setFrom} type="select" options={[
            { value: 'USD', label: 'Dólar (USD)' }, { value: 'EUR', label: 'Euro (EUR)' }, { value: 'BRL', label: 'Real (BRL)' }, { value: 'GBP', label: 'Libra (GBP)' }, { value: 'BTC', label: 'Bitcoin (BTC)' }
          ]} />
          <FormField label="Para" value={to} onChange={setTo} type="select" options={[
            { value: 'BRL', label: 'Real (BRL)' }, { value: 'USD', label: 'Dólar (USD)' }, { value: 'EUR', label: 'Euro (EUR)' }
          ]} />
        </div>
        <div className="flex justify-center mt-8 gap-4">
          <button onClick={fetchRates} className="p-4 bg-theme-input rounded-xl border border-theme-border hover:bg-theme-result transition-all">
            <Icon name="refresh-cw" className={`w-5 h-5 text-theme-muted ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={convert} className="px-10 py-4 bg-accent-gradient text-white rounded-xl font-bold shadow-accent hover:scale-105 transition-all">Converter</button>
        </div>
      </div>

      {result && (
        <div className="max-w-md mx-auto">
          <ResultCard title={`Total em ${result.to}`} value={`${result.to} ${result.converted.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`} color="emerald" />
          <p className="text-center text-[10px] text-theme-muted mt-4 uppercase font-black tracking-widest">Câmbio: 1 {result.from} = {result.rate.toFixed(4)} {result.to}</p>
        </div>
      )}
    </div>
  );
};

export default CurrencyConverter;
